import { compare } from './object-utils'
import {
  DemResource,
  OrthomosaicResource,
  PlantCountsResource,
  ProjectMetadata,
  ProjectResource,
  SourceImageyResource,
  SourceImageryFeature,
} from './types/project'

const isOrtho = (r: ProjectResource): r is OrthomosaicResource =>
  r.type === 'orthomosaic'
const isDem = (r: ProjectResource): r is DemResource => r.type === 'dem'
const isSourceImagery = (r: ProjectResource): r is SourceImageyResource =>
  r.type === 'source_imagery'
const isPlantCounts = (r: ProjectResource): r is PlantCountsResource =>
  r.type === 'plant_counts'

export default class ProjectResources {
  constructor(metadata: ProjectMetadata) {
    const resources = metadata && metadata.resources

    for (const resourceMapper of resourceMappers) {
      const resource = resources?.find(resourceMapper.mapperFn)
      ;(this[resourceMapper.property] as any) =
        resource || resourceMapper.default
    }

    if (
      !this.sourceImagery &&
      metadata?.image_geojson?.features.every(f => f.properties.filename)
    ) {
      this.sourceImagery = {
        id: '8c1c5c23-e786-4ba9-8226-63963f6b0a99',
        type: 'source_imagery',
        geojson: metadata.image_geojson,
      }
    }
  }

  orthoPhoto?: OrthomosaicResource
  dem?: DemResource
  sourceImagery?: SourceImageyResource
  plantCounts?: PlantCountsResource

  hasOrthomosaic() {
    return !!this.orthoPhoto
  }

  hasDem() {
    return !!this.dem
  }

  hasSourceImagery() {
    return !!this.sourceImagery
  }
}

export const sortImageFeatures = (features: SourceImageryFeature[]) => {
  return features.sort((a, b) =>
    a.properties.datetime && b.properties.datetime
      ? compare(a.properties.datetime, b.properties.datetime)
      : compare(a.properties.filename, b.properties.filename)
  )
}

type ResourceMapperFn<T extends ProjectResource> = (
  r: ProjectResource
) => r is T
type ResourceMapper<T extends ProjectResource> = {
  property: keyof ProjectResources
  mapperFn: ResourceMapperFn<T>
  default?: T
}

type ResourceMappers =
  | ResourceMapper<OrthomosaicResource>
  | ResourceMapper<DemResource>
  | ResourceMapper<SourceImageyResource>
  | ResourceMapper<PlantCountsResource>

const resourceMappers: ResourceMappers[] = [
  {
    property: 'orthoPhoto',
    mapperFn: isOrtho,
    default: {
      id: '2389ded1-c03c-413d-9524-1c4424815a0f',
      type: 'orthomosaic',
      path: 'results/ortho.tiff',
    },
  },
  {
    property: 'dem',
    mapperFn: isDem,
    default: {
      id: '842916cc-1278-4ef9-a44a-37b2b60ccbee',
      type: 'dem',
      path: 'results/dem.tiff',
    },
  },
  {
    property: 'sourceImagery',
    mapperFn: isSourceImagery,
  },
  {
    property: 'plantCounts',
    mapperFn: isPlantCounts,
  },
]
